import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button, Nav, Form, Dropdown } from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { TriangleAlert, Ellipsis } from 'lucide-react';
import { lockedSignupDomainService, signupDomainService, ocipIpAllowlistService } from '../services/apiService';
import StackedTable from '../components/StackedTable';
import { useIsMobile, useMediaQuery, BELOW_DESKTOP_QUERY } from '../hooks/useMediaQuery';
import { useAuthToken } from '../hooks/useAuthToken';
import { usePendingApprovals } from '../context/PendingApprovalsContext';
import { usePermissions } from '../context/PermissionsContext';
import { useConfirm } from '../components/ConfirmDialog';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { formatDate, plural, statusLabel, EMPTY } from '../utils/format';

/**
 * Staff view of every domain and check-in IP address request across all companies. Four tabs: what
 * is waiting for a decision, every locked domain, every sign-up domain and every check-in IP address.
 * Sign-up domains auto-join strangers into an account and locked domains stop them creating one, so
 * both need the company to actually own the domain. The matching-users count, the other-company
 * count and the conflict flag are the hints. A check-in IP address lets anyone at that address check
 * in as the company's users, so it must belong to the customer or their internet provider: the
 * registered owner and other companies using the address are the hints.
 */
const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'locked', label: 'Locked domains' },
  { key: 'signup', label: 'Sign-up domains' },
  { key: 'ip-addresses', label: 'Check-in IP addresses' },
];

const companyOf = (row) => row.companyName || row.companyId;

// "1 user has", "3 users have"
const usersHave = (count) => `${plural(count, 'user')} ${count === 1 ? 'has' : 'have'}`;

// A small outline button for the row's overflow menu, without the dropdown caret
const OverflowToggle = forwardRef(({ className, buttonClassName, children, ...props }, ref) => (
  <Button ref={ref} variant="outline-secondary" size="sm" className={buttonClassName} {...props}>
    {children}
  </Button>
));
OverflowToggle.displayName = 'OverflowToggle';

// Muted check results with one attention icon when any of them needs a second look
const Checks = ({ warning, children }) => (
  <div className="d-flex gap-2 small text-muted">
    {warning && (
      <>
        <TriangleAlert size={14} className="text-warning flex-shrink-0" style={{ marginTop: 2 }} aria-hidden="true" />
        <span className="visually-hidden">Needs a second look:</span>
      </>
    )}
    <div className="text-start">{children}</div>
  </div>
);

const PendingApprovals = () => {
  const ensureToken = useAuthToken();
  const { canEdit } = usePermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'pending';

  const [signupDomains, setSignupDomains] = useState([]);
  const [lockedDomains, setLockedDomains] = useState([]);
  const [ipAddresses, setIpAddresses] = useState([]);
  // Owner lookups for addresses with no stored owner yet, keyed by row id
  const [ownerChecks, setOwnerChecks] = useState({});
  const [loading, setLoading] = useState(true);
  // Set once every list has loaded, so a failed load is never handed to the nav as "nothing waiting"
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // A failed approve, reject or remove
  const [actionError, setActionError] = useState(null);
  const { setCounts: setPendingCounts } = usePendingApprovals();
  // The row and the button that is working: { key, action }
  const [busy, setBusy] = useState(null);
  const [warningsOnly, setWarningsOnly] = useState(searchParams.get('warnings') === '1');
  // Below desktop the tables drop secondary columns and let check text and buttons wrap, so the actions stay on screen
  const isMobile = useIsMobile();
  const belowDesktop = useMediaQuery(BELOW_DESKTOP_QUERY);
  const buttonGap = belowDesktop ? undefined : 'me-2';
  // Only the pending tab is for deciding; the full lists also offer Remove, tucked into a menu
  const showRemove = activeTab !== 'pending';

  // A row needs a second look when the domain is claimed elsewhere, nobody at the company uses it,
  // or people at other companies do.
  const hasWarning = (row) => !!row.conflict || row.matchingUsers === 0 || row.otherCompanyUsers > 0;
  const applyWarningsFilter = (rows) => (warningsOnly ? rows.filter(hasWarning) : rows);
  // An IP address needs a second look when another company has it on their list too.
  const ipHasWarning = (row) => row.otherCompanies?.length > 0;
  const applyIpWarningsFilter = (rows) => (warningsOnly ? rows.filter(ipHasWarning) : rows);

  const toggleWarningsOnly = (checked) => {
    setWarningsOnly(checked);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (checked) next.set('warnings', '1'); else next.delete('warnings');
      return next;
    }, { replace: true });
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      await ensureToken();
      const [signup, locked, ip] = await Promise.all([
        signupDomainService.listAll(),
        lockedSignupDomainService.listAll(),
        ocipIpAllowlistService.listAll(),
      ]);
      setSignupDomains(signup.data);
      setLockedDomains(locked.data);
      setIpAddresses(ip.data);
      setLoaded(true);
    } catch (err) {
      setLoadError(serverMessage(err, 'Could not load the requests.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [ensureToken]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setTab = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'pending') next.delete('tab'); else next.set('tab', key);
      return next;
    });
  };

  const serviceFor = (kind) => (kind === 'signup' ? signupDomainService : lockedSignupDomainService);
  const setListFor = (kind) => (kind === 'signup' ? setSignupDomains : setLockedDomains);

  // Runs one row action with its button marked busy, reporting a failure above the tables
  const runAction = async (key, action, work, failure) => {
    try {
      setBusy({ key, action });
      setActionError(null);
      await ensureToken();
      await work();
    } catch (err) {
      setActionError(serverMessage(err, failure));
      console.error(err);
    } finally {
      setBusy(null);
    }
  };

  // What the domain's checks say, repeated in the confirmation
  const domainCheckList = (row) => (
    <ul className="mb-0 ps-3">
      <li>
        {row.matchingUsers === 0
          ? `Nobody at ${companyOf(row)} has an @${row.domain} email address. Check the company owns the domain.`
          : `${usersHave(row.matchingUsers)} an @${row.domain} email address at ${companyOf(row)}.`}
      </li>
      {row.otherCompanyUsers > 0 && <li>{usersHave(row.otherCompanyUsers)} this email domain in other companies.</li>}
      {row.conflict && <li>Conflict: {row.conflict}</li>}
    </ul>
  );

  const decide = async (kind, row, decision) => {
    if (decision === 'approve') {
      const effect = kind === 'signup'
        ? `Anyone who signs up with an @${row.domain} email address will join ${companyOf(row)} automatically.`
        : `Nobody with an @${row.domain} email address will be able to create a separate account.`;
      const approved = await confirm({
        title: `Approve ${row.domain} for ${companyOf(row)}?`,
        body: (
          <>
            <p>{effect}</p>
            {domainCheckList(row)}
          </>
        ),
        confirmLabel: 'Approve domain',
      });
      if (!approved) return;
    }
    await runAction(`${kind}-${row.id}`, decision, async () => {
      const response = decision === 'approve'
        ? await serviceFor(kind).approve(row.companyId, row.id)
        : await serviceFor(kind).reject(row.companyId, row.id);
      setListFor(kind)((list) => list.map((d) => (d.id === row.id ? response.data : d)));
    }, `Could not ${decision} ${row.domain}.`);
  };

  const remove = async (kind, row) => {
    let effect = `This deletes the request. Nothing changes for anyone at ${companyOf(row)}.`;
    if (row.status === 'APPROVED') {
      effect = kind === 'signup'
        ? `People who sign up with an @${row.domain} email address will no longer join ${companyOf(row)} automatically.`
        : `People with an @${row.domain} email address will be able to create separate accounts again.`;
    }
    const removed = await confirm({
      title: `Remove ${row.domain} from ${companyOf(row)}?`,
      body: (
        <>
          <p>{effect}</p>
          {domainCheckList(row)}
        </>
      ),
      confirmLabel: 'Remove domain',
      variant: 'danger',
    });
    if (!removed) return;
    await runAction(`${kind}-${row.id}`, 'remove', async () => {
      await serviceFor(kind).remove(row.companyId, row.id);
      setListFor(kind)((list) => list.filter((d) => d.id !== row.id));
    }, `Could not remove ${row.domain}.`);
  };

  // What the address's checks say, repeated in the confirmation
  const ipCheckList = (row) => {
    const check = ownerChecks[row.id]?.result;
    const owner = row.owner || check?.owner;
    const others = row.otherCompanies?.length ? row.otherCompanies : check?.otherCompanies;
    return (
      <ul className="mb-0 ps-3">
        <li>{owner ? `Registered owner: ${owner}.` : 'The owner has not been looked up yet. Use Check owner first.'}</li>
        {others?.length > 0 && <li>Also listed for {others.map((other) => other.companyName || other.companyId).join(', ')}.</li>}
      </ul>
    );
  };

  const decideIpAddress = async (row, decision) => {
    if (decision === 'approve') {
      const approved = await confirm({
        title: `Approve ${row.ipAddress} for ${companyOf(row)}?`,
        body: (
          <>
            <p>
              Check-ins from this address will be accepted for {companyOf(row)} within a minute. Only approve it if it
              belongs to the customer or their internet provider.
            </p>
            {ipCheckList(row)}
          </>
        ),
        confirmLabel: 'Approve address',
      });
      if (!approved) return;
    }
    await runAction(`ip-${row.id}`, decision, async () => {
      const response = decision === 'approve'
        ? await ocipIpAllowlistService.approve(row.companyId, row.id)
        : await ocipIpAllowlistService.reject(row.companyId, row.id);
      setIpAddresses((list) => list.map((a) => (a.id === row.id ? response.data : a)));
    }, `Could not ${decision} ${row.ipAddress}.`);
  };

  const removeIpAddress = async (row) => {
    const removed = await confirm({
      title: `Remove ${row.ipAddress} from ${companyOf(row)}?`,
      body: (
        <>
          <p>
            {row.status === 'APPROVED'
              ? 'Check-ins from this address will stop being accepted within a minute.'
              : 'This deletes the request. Nothing changes for check-ins today.'}
          </p>
          {ipCheckList(row)}
        </>
      ),
      confirmLabel: 'Remove address',
      variant: 'danger',
    });
    if (!removed) return;
    await runAction(`ip-${row.id}`, 'remove', async () => {
      await ocipIpAllowlistService.remove(row.companyId, row.id);
      setIpAddresses((list) => list.filter((a) => a.id !== row.id));
    }, `Could not remove ${row.ipAddress}.`);
  };

  const checkOwner = async (row) => {
    try {
      setOwnerChecks((prev) => ({ ...prev, [row.id]: { loading: true } }));
      await ensureToken();
      const response = await ocipIpAllowlistService.lookup(row.companyId, row.ipAddress);
      setOwnerChecks((prev) => ({ ...prev, [row.id]: { result: response.data } }));
    } catch (err) {
      setOwnerChecks((prev) => ({ ...prev, [row.id]: { error: serverMessage(err, 'Lookup failed') } }));
      console.error(err);
    }
  };

  const statusBadge = (status) => (
    <Badge bg={status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'danger' : 'warning'}>
      {statusLabel(status)}
    </Badge>
  );

  const companyCell = (row) => (
    <td className="align-middle">
      <Link className="cell-link" to={`/companies/${row.companyId}?tab=config`} title="Open this company's configuration">
        {companyOf(row)}
      </Link>
    </td>
  );

  // Phones: a row of buttons that wraps in the card. Tablets: a column of buttons, which keeps the table narrow.
  const actionsCell = (buttons) => {
    if (isMobile) {
      return <td className="align-middle"><div className="d-flex flex-wrap justify-content-end gap-2">{buttons}</div></td>;
    }
    if (belowDesktop) {
      return <td className="align-middle"><div className="d-grid gap-1 text-nowrap">{buttons}</div></td>;
    }
    return <td className="align-middle text-end text-nowrap">{buttons}</td>;
  };

  // The label of the button that is working turns into a spinner; the row's other buttons are disabled
  const buttonLabel = (key, action, label) => (
    busy?.key === key && busy.action === action
      ? <><Spinner size="sm" animation="border" aria-hidden="true" /><span className="visually-hidden">{label}</span></>
      : label
  );

  const actionButtons = ({ key, name, canApprove, canReject, onApprove, onReject, onRemove }) => {
    const rowBusy = busy?.key === key;
    return (
      <>
        {canApprove && (
          <Button variant="outline-success" size="sm" className={buttonGap} disabled={rowBusy} onClick={onApprove}>
            {buttonLabel(key, 'approve', 'Approve')}
          </Button>
        )}
        {canReject && (
          <Button variant="outline-danger" size="sm" className={buttonGap} disabled={rowBusy} onClick={onReject}>
            {buttonLabel(key, 'reject', 'Reject')}
          </Button>
        )}
        {showRemove && (
          <Dropdown align="end" className={isMobile ? undefined : 'd-inline-block'}>
            <Dropdown.Toggle
              as={OverflowToggle}
              id={`more-${key}`}
              disabled={rowBusy}
              aria-label={`More actions for ${name}`}
              buttonClassName={belowDesktop && !isMobile ? 'w-100' : undefined}
            >
              {busy?.key === key && busy.action === 'remove'
                ? <Spinner size="sm" animation="border" aria-hidden="true" />
                : <Ellipsis size={16} aria-hidden="true" />}
            </Dropdown.Toggle>
            {/* Fixed, so the menu is not clipped by the table's scroll container */}
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
              <Dropdown.Item as="button" onClick={onRemove}>Remove</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </>
    );
  };

  const renderDomainChecks = (row) => (
    <Checks warning={hasWarning(row)}>
      <div>{row.matchingUsers === 0 ? 'No matching users' : plural(row.matchingUsers, 'matching user')}</div>
      {row.conflict && <div>Conflict: {row.conflict}</div>}
      {row.otherCompanyUsers > 0 && (
        <div>
          <Link to={`/reports/shared-email-domains?domain=${encodeURIComponent(row.domain)}`} title="See which companies share this domain">
            {plural(row.otherCompanyUsers, 'user')} in other companies
          </Link>
        </div>
      )}
    </Checks>
  );

  const renderTable = (kind, rows, emptyText) => (
    rows.length === 0 ? (
      <div className="p-4 text-center text-muted">{emptyText}</div>
    ) : (
      <StackedTable responsive className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Company</th>
            {/* Set widths below desktop, so long domains and checks wrap instead of pushing the actions off screen */}
            <th style={belowDesktop ? { width: 200 } : undefined}>Domain</th>
            <th>Status</th>
            <th style={belowDesktop ? { width: 180 } : undefined}>Checks</th>
            <th className="d-md-none d-lg-table-cell">Requested</th>
            <th className="d-none d-lg-table-cell">Decided</th>
            {canEdit && <th className={isMobile || belowDesktop ? undefined : 'text-end'}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = `${kind}-${row.id}`;
            return (
              <tr key={key}>
                {companyCell(row)}
                <td className="align-middle"><code>{row.domain}</code></td>
                <td className="align-middle">{statusBadge(row.status)}</td>
                <td className="align-middle">{renderDomainChecks(row)}</td>
                <td className="align-middle d-md-none d-lg-table-cell">{formatDate(row.createdAt)}</td>
                <td className="align-middle d-none d-lg-table-cell">
                  {formatDate(row.decidedAt)}
                  {row.decidedBy && <span className="text-muted small"> by {row.decidedBy}</span>}
                </td>
                {canEdit && actionsCell(actionButtons({
                  key,
                  name: row.domain,
                  canApprove: row.status !== 'APPROVED',
                  canReject: row.status !== 'REJECTED',
                  onApprove: () => decide(kind, row, 'approve'),
                  onReject: () => decide(kind, row, 'reject'),
                  onRemove: () => remove(kind, row),
                }))}
              </tr>
            );
          })}
        </tbody>
      </StackedTable>
    )
  );

  const otherCompaniesCheck = (otherCompanies) => (
    otherCompanies?.length > 0 && (
      <div className="mt-1" title="A shared address is often a shared building, co-working space or security gateway">
        <Checks warning>
          Also listed for {otherCompanies.map((other) => other.companyName || other.companyId).join(', ')}
        </Checks>
      </div>
    )
  );

  // The stored owner, or a button to look it up for requests that arrive without one
  const renderOwner = (row) => {
    const check = ownerChecks[row.id];
    if (row.owner) {
      return <>{row.owner}{otherCompaniesCheck(row.otherCompanies)}</>;
    }
    if (check?.result) {
      return <><strong>{check.result.owner}</strong>{otherCompaniesCheck(check.result.otherCompanies)}</>;
    }
    return (
      <>
        <Button variant="outline-secondary" size="sm" disabled={check?.loading} onClick={() => checkOwner(row)}>
          {check?.loading ? <Spinner size="sm" animation="border" /> : 'Check owner'}
        </Button>
        {check?.error && <div className="text-danger small">{check.error}</div>}
        {otherCompaniesCheck(row.otherCompanies)}
      </>
    );
  };

  const renderIpTable = (rows, emptyText) => (
    rows.length === 0 ? (
      <div className="p-4 text-center text-muted">{emptyText}</div>
    ) : (
      <StackedTable responsive className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Company</th>
            {/* A set width below desktop, so an address only wraps when it is too long for it */}
            <th style={belowDesktop ? { width: 180 } : undefined}>IP address</th>
            <th className="d-md-none d-lg-table-cell">Office</th>
            <th className="d-md-none d-xl-table-cell">Requested by</th>
            <th className="d-none d-xl-table-cell">Requested</th>
            <th>Status</th>
            <th>Owner</th>
            {canEdit && <th className={isMobile || belowDesktop ? undefined : 'text-end'}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = `ip-${row.id}`;
            return (
              <tr key={key}>
                {companyCell(row)}
                <td className="align-middle">
                  <code>{row.ipAddress}</code>
                  {row.label && <div className="text-muted small">{row.label}</div>}
                </td>
                <td className="align-middle d-md-none d-lg-table-cell">{row.officeName || EMPTY}</td>
                <td className="align-middle d-md-none d-xl-table-cell">
                  {row.requestedByEmail || row.requestedByUserId || (row.createdBy
                    ? <>{row.createdBy} <span className="text-muted small">(staff)</span></>
                    : EMPTY)}
                </td>
                <td className="align-middle d-none d-xl-table-cell">{formatDate(row.createdAt)}</td>
                <td className="align-middle">
                  {statusBadge(row.status)}
                  {row.decidedBy && <div className="text-muted small">{formatDate(row.decidedAt)} by {row.decidedBy}</div>}
                </td>
                <td className="align-middle">{renderOwner(row)}</td>
                {canEdit && actionsCell(actionButtons({
                  key,
                  name: row.ipAddress,
                  canApprove: row.status === 'PENDING',
                  canReject: row.status === 'PENDING',
                  onApprove: () => decideIpAddress(row, 'approve'),
                  onReject: () => decideIpAddress(row, 'reject'),
                  onRemove: () => removeIpAddress(row),
                }))}
              </tr>
            );
          })}
        </tbody>
      </StackedTable>
    )
  );

  const pendingSignup = signupDomains.filter((d) => d.status === 'PENDING');
  const pendingLocked = lockedDomains.filter((d) => d.status === 'PENDING');
  const pendingIp = ipAddresses.filter((a) => a.status === 'PENDING');
  const pendingCount = pendingSignup.length + pendingLocked.length + pendingIp.length;

  // Keep the nav badge and home page in step with the decisions made here, without loading the counts again
  useEffect(() => {
    if (loaded) setPendingCounts({ signup: pendingSignup.length, locked: pendingLocked.length, ipAddresses: pendingIp.length });
  }, [loaded, pendingSignup.length, pendingLocked.length, pendingIp.length, setPendingCounts]);

  // Warnings on the open tab only, so the count matches what the switch would show
  const warningCount = activeTab === 'pending'
    ? pendingSignup.filter(hasWarning).length + pendingLocked.filter(hasWarning).length + pendingIp.filter(ipHasWarning).length
    : activeTab === 'locked' ? lockedDomains.filter(hasWarning).length
      : activeTab === 'signup' ? signupDomains.filter(hasWarning).length
        : ipAddresses.filter(ipHasWarning).length;

  const tabCount = {
    pending: pendingCount,
    locked: lockedDomains.length,
    signup: signupDomains.length,
    'ip-addresses': ipAddresses.length,
  };

  const shownPendingSignup = applyWarningsFilter(pendingSignup);
  const shownPendingLocked = applyWarningsFilter(pendingLocked);
  const shownPendingIp = applyIpWarningsFilter(pendingIp);
  const shownLocked = applyWarningsFilter(lockedDomains);
  const shownSignup = applyWarningsFilter(signupDomains);
  const shownIp = applyIpWarningsFilter(ipAddresses);

  const renderContent = () => {
    if (loadError && !loaded) return <ErrorState message={loadError} onRetry={fetchAll} />;
    if (loading && !loaded) return <LoadingState label="Loading requests" />;
    if (activeTab === 'pending') {
      return (
        <>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Sign-up domains waiting ({shownPendingSignup.length})</h5>
              <small className="text-muted">Once approved, anyone signing up with this email domain joins the company automatically.</small>
            </Card.Header>
            <Card.Body className="p-0">
              {renderTable('signup', shownPendingSignup, warningsOnly ? 'No waiting sign-up domains have a warning' : 'No sign-up domains waiting for approval')}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Locked domains waiting ({shownPendingLocked.length})</h5>
              <small className="text-muted">Once approved, nobody with this email domain can create a separate account.</small>
            </Card.Header>
            <Card.Body className="p-0">
              {renderTable('locked', shownPendingLocked, warningsOnly ? 'No waiting locked domains have a warning' : 'No locked domains waiting for approval')}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Check-in IP addresses waiting ({shownPendingIp.length})</h5>
              <small className="text-muted">
                Once approved, office check-ins from this address are accepted for the company. Check the owner first and never approve
                cloud Wi-Fi or security gateway addresses (Zscaler, Netskope, Cloudflare, Prisma Access, AWS, Azure, Google Cloud).
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              {renderIpTable(shownPendingIp, warningsOnly ? 'No waiting IP addresses have a warning' : 'No check-in IP addresses waiting for approval')}
            </Card.Body>
          </Card>
        </>
      );
    }
    if (activeTab === 'locked') {
      return (
        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h5 className="mb-0">All locked domains ({shownLocked.length})</h5>
            <small className="text-muted">Every locked domain across every company, newest first. Only approved domains block sign-ups.</small>
          </Card.Header>
          <Card.Body className="p-0">
            {renderTable('locked', shownLocked, warningsOnly ? 'No locked domains have a warning' : 'No company has locked a domain yet')}
          </Card.Body>
        </Card>
      );
    }
    if (activeTab === 'ip-addresses') {
      return (
        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h5 className="mb-0">All check-in IP addresses ({shownIp.length})</h5>
            <small className="text-muted">Every check-in IP address across every company, newest first. Only approved addresses are accepted for check-ins.</small>
          </Card.Header>
          <Card.Body className="p-0">
            {renderIpTable(shownIp, warningsOnly ? 'No IP addresses have a warning' : 'No company has a check-in IP address yet')}
          </Card.Body>
        </Card>
      );
    }
    return (
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">All sign-up domains ({shownSignup.length})</h5>
          <small className="text-muted">Every sign-up domain across every company, newest first. Only approved domains auto-join, and only while the company's switch is on.</small>
        </Card.Header>
        <Card.Body className="p-0">
          {renderTable('signup', shownSignup, warningsOnly ? 'No sign-up domains have a warning' : 'No company has linked a sign-up domain yet')}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container fluid className="py-4">
      {confirmDialog}
      <Row className="mb-4">
        <Col>
          <h1>Approvals</h1>
          <p className="text-muted">Sign-up domain, locked domain and check-in IP address requests across every company. Confirm the company owns a domain or address before approving it.</p>
        </Col>
      </Row>

      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError(null)}>{actionError}</Alert>}

      <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
        <Nav variant="tabs" activeKey={activeTab} onSelect={setTab} className="flex-grow-1">
          {TABS.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key}>
                {tab.label}
                {/* Counts only once loaded, so a load still running or failed never reads as "none" */}
                {loaded && (tab.key !== 'pending' || pendingCount > 0) && (
                  <Badge bg={tab.key === 'pending' ? 'warning' : 'secondary'} pill className="ms-2">{tabCount[tab.key]}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Form.Check
          type="switch"
          id="warningsOnly"
          className="mb-2"
          label={<>Warnings only{loaded && warningCount > 0 && <Badge bg="warning" pill className="ms-2">{warningCount}</Badge>}</>}
          checked={warningsOnly}
          onChange={(e) => toggleWarningsOnly(e.target.checked)}
          title="Show only rows with a conflict, no matching users, users in another company, or an IP address another company also lists"
        />
      </div>

      {renderContent()}
    </Container>
  );
};

export default PendingApprovals;
